import React, { useState } from 'react';
import { useDashboardData } from '../../hooks/useDashboardData';
import { Sparkles, X, ChevronRight, TrendingUp, TrendingDown, Target, ShieldAlert } from 'lucide-react';
import { useSettingsStore } from '../../stores/settingsStore';

export const AITradeIdeasWidget: React.FC = () => {
    const { analysis, marketData } = useDashboardData();
    const activeSymbol = useSettingsStore(s => s.symbol);
    const indexLtp = marketData?.spotPrice || 0;
    const [isOpen, setIsOpen] = useState(false);

    if (!analysis) return null;

    const {
        shift,
        supportStrike,
        resistanceStrike,
        variance,
        ceOtmStrike,
        peOtmStrike
    } = analysis;

    // AI Rules-based Logic
    const finalVariance = variance?.finalVariance || 0;
    const totalCount = variance?.totalCount || 0;

    let sentiment = 'Neutral';
    let bias = 'sideways';
    let recommendation = 'Wait for clearer signals.';
    let strategies = [];

    // Rule 1: High Bullish Variance
    if (finalVariance > 20 && totalCount > 10) {
        sentiment = 'Strongly Bullish';
        bias = 'bullish';
        recommendation = 'Market is exhibiting strong upward momentum with significant put writing.';
        strategies.push({
            name: 'Bull Put Spread',
            details: `Sell ${supportStrike} PE / Buy ${peOtmStrike} PE for protection.`,
            risk: 'Low to Medium',
            icon: <TrendingUp className="w-5 h-5 text-emerald-500" />
        });
        strategies.push({
            name: 'Naked Call Buy (High Risk)',
            details: `Buy ATM or slightly OTM Call if price crosses above Shift (${shift?.toFixed(2)}).`,
            risk: 'High',
            icon: <Target className="w-5 h-5 text-emerald-500" />
        });
    } 
    // Rule 2: High Bearish Variance
    else if (finalVariance < -20 && totalCount < -10) {
        sentiment = 'Strongly Bearish';
        bias = 'bearish';
        recommendation = 'Heavy call writing indicates strong resistance at upper levels.';
        strategies.push({
            name: 'Bear Call Spread',
            details: `Sell ${resistanceStrike} CE / Buy ${ceOtmStrike} CE for protection.`,
            risk: 'Low to Medium',
            icon: <TrendingDown className="w-5 h-5 text-rose-500" />
        });
        strategies.push({
            name: 'Naked Put Buy (High Risk)',
            details: `Buy ATM or slightly OTM Put if price crosses below Shift (${shift?.toFixed(2)}).`,
            risk: 'High',
            icon: <Target className="w-5 h-5 text-rose-500" />
        });
    }
    // Rule 3: Divergence / Smart Money Trap (Variance Bullish, Count Bearish)
    else if (finalVariance > 15 && totalCount < -5) {
        sentiment = 'Smart Money Trap (Potential Fakeout)';
        bias = 'bearish'; // Fakeout upwards, reality is downwards
        recommendation = 'Variance shows buying, but total count shows selling pressure. Be cautious of a bull trap.';
        strategies.push({
            name: 'Iron Condor',
            details: `Sell ${resistanceStrike} CE & ${supportStrike} PE, buy wings for protection.`,
            risk: 'Medium',
            icon: <ShieldAlert className="w-5 h-5 text-amber-500" />
        });
    }
    // Rule 4: Sideways / Range Bound
    else {
        sentiment = 'Range Bound';
        bias = 'sideways';
        recommendation = 'Market lacks clear directional conviction. Option selling strategies are preferred.';
        strategies.push({
            name: 'Short Strangle',
            details: `Sell ${resistanceStrike} CE and ${supportStrike} PE. Strict stop loss if ranges break.`,
            risk: 'High',
            icon: <TrendingUp className="w-5 h-5 text-amber-500" />
        });
    }

    const biasColor = bias === 'bullish' ? 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20' 
                    : bias === 'bearish' ? 'text-rose-500 bg-rose-500/10 border-rose-500/20'
                    : 'text-amber-500 bg-amber-500/10 border-amber-500/20';

    return (
        <>
            {/* The Trigger Button */}
            <button
                onClick={() => setIsOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white text-xs font-semibold rounded shadow-sm shadow-violet-500/25 transition-all active:scale-95"
            >
                <Sparkles className="w-3.5 h-3.5" />
                AI Trade Ideas
            </button>

            {/* The Modal */}
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-violet-600/10 to-fuchsia-600/10">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-violet-500" />
                                <h3 className="font-bold text-lg text-slate-800 dark:text-slate-200">
                                    AI Trade Ideas: <span className="text-violet-600 dark:text-violet-400">{activeSymbol}</span>
                                </h3>
                            </div>
                            <button onClick={() => setIsOpen(false)} className="p-1 rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 flex flex-col gap-6">
                            {/* Market Context */}
                            <div className="flex flex-col gap-2">
                                <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Current Market Context</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800">
                                        <div className="text-xs text-slate-500 mb-1">Index Spot</div>
                                        <div className="font-mono font-semibold text-slate-700 dark:text-slate-300">{indexLtp?.toFixed(2)}</div>
                                    </div>
                                    <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800">
                                        <div className="text-xs text-slate-500 mb-1">Shift Level</div>
                                        <div className="font-mono font-semibold text-slate-700 dark:text-slate-300">{shift?.toFixed(2)}</div>
                                    </div>
                                    <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800">
                                        <div className="text-xs text-slate-500 mb-1">Support</div>
                                        <div className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">{supportStrike}</div>
                                    </div>
                                    <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800">
                                        <div className="text-xs text-slate-500 mb-1">Resistance</div>
                                        <div className="font-mono font-semibold text-rose-600 dark:text-rose-400">{resistanceStrike}</div>
                                    </div>
                                </div>
                            </div>

                            {/* AI Analysis */}
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">AI Analysis</h4>
                                    <span className={`px-2.5 py-1 text-xs font-bold rounded-full border ${biasColor}`}>
                                        {sentiment}
                                    </span>
                                </div>
                                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800">
                                    {recommendation}
                                </p>
                            </div>

                            {/* Strategy Ideas */}
                            <div className="flex flex-col gap-3">
                                <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Suggested Strategies</h4>
                                <div className="flex flex-col gap-3">
                                    {strategies.map((strat, idx) => (
                                        <div key={idx} className="flex gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-violet-300 dark:hover:border-violet-600 transition-colors group">
                                            <div className="flex-shrink-0 mt-0.5">
                                                {strat.icon}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-1">
                                                    <h5 className="font-semibold text-slate-800 dark:text-slate-200">{strat.name}</h5>
                                                    <span className="text-xs font-medium text-slate-500 px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded-md">
                                                        Risk: {strat.risk}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                                    {strat.details}
                                                </p>
                                            </div>
                                            <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <ChevronRight className="w-5 h-5 text-slate-400" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
